import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SensitiveData } from "@/components/sensitive-data";
import { formatCurrency, formatDate } from "@/lib/utils";
import type { ForecastPerformance } from "../types";

interface RevenueForecastCardProps {
  performance: ForecastPerformance;
  onViewForecast: () => void;
}

export function RevenueForecastCard({ performance, onViewForecast }: RevenueForecastCardProps) {
  const { summary, rows, revenuePositive } = performance;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Revenue Forecast</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {summary
            ? <SensitiveData>{formatCurrency(summary.totalForecastRevenue)}</SensitiveData>
            : <span className="text-muted-foreground">—</span>
          }
        </div>
        <p className="text-xs text-muted-foreground mt-1">Total Forecast Revenue</p>
        {summary ? (
          <div className="mt-3 space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Actual Invoiced</span>
              <SensitiveData><span className="font-medium">{formatCurrency(rows.actualInvoiced)}</span></SensitiveData>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Revenue Variance</span>
              <SensitiveData>
                <span className={`font-medium ${revenuePositive ? 'text-success-600' : 'text-error-600'}`}>
                  {revenuePositive ? '+' : ''}
                  {rows.revenueVariance < 0
                    ? `−${formatCurrency(Math.abs(rows.revenueVariance))}`
                    : formatCurrency(rows.revenueVariance)
                  }{' '}
                  ({rows.revenueVariancePercent.toFixed(1)}%)
                </span>
              </SensitiveData>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Forecast End Date</span>
              <span className="font-medium">{formatDate(rows.forecastEndDate)}</span>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground italic">No forecast data committed yet.</p>
        )}
        <div className="mt-3 pt-2 border-t">
          <button
            type="button"
            onClick={onViewForecast}
            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
          >
            View Forecast →
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
